import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Ban, Check, ShieldCheck, ShieldOff, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '@/api/client'
import { users as api } from '@/api/endpoints'
import { Alert, EmptyState, Field, Input, Modal, Select, Spinner } from '@/components/ui'
import { formatDate } from '@/lib/utils'
import { useAuth } from '@/store/auth'
import type { Role, User } from '@/types/api'

const ROLES: Role[] = ['owner', 'admin', 'manager', 'viewer']

/** SettingsPresenter::actionUsers */
export default function UsersTab() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const me = useAuth((s) => s.user)

  const [editing, setEditing] = useState<User | 'new' | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.list({ limit: 200 }),
  })

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['users'] })
  const block = useMutation({ mutationFn: (id: number) => api.block(id), onSuccess: invalidate })
  const unblock = useMutation({ mutationFn: (id: number) => api.unblock(id), onSuccess: invalidate })
  // Stratený telefón aj záložné kódy - správca MFA zruší, používateľ si ho
  // zapne znova. Vlastníkovi ho zruší len vlastník (stráži aj server).
  const resetMfa = useMutation({
    mutationFn: (id: number) => api.resetMfa(id),
    onSuccess: invalidate,
    onError: (err) => window.alert(apiErrorMessage(err)),
  })

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center text-slate-400">
        <Spinner className="h-5 w-5" />
      </div>
    )
  }

  return (
    <div className="max-w-4xl space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-400">{data?.count ?? 0}</span>
        <button type="button" className="btn-primary ml-auto" onClick={() => setEditing('new')}>
          <UserPlus size={15} />
          {t('settings.newUser')}
        </button>
      </div>

      <div className="card overflow-x-auto">
        {(data?.results.length ?? 0) === 0 ? (
          <EmptyState>{t('app.empty')}</EmptyState>
        ) : (
          <table className="table-base">
            <thead>
              <tr>
                <th>{t('settings.usersName')}</th>
                <th>{t('auth.email')}</th>
                <th>{t('settings.role')}</th>
                <th>{t('changelog.created')}</th>
                <th className="w-20">{t('global.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {data?.results.map((user) => (
                <tr key={user.id} className={user.block ? 'opacity-50' : ''}>
                  <td className="font-medium">
                    {user.full_name || '—'}
                    {user.id === me?.id ? (
                      <span className="ml-1 text-xs text-slate-400">({t('settings.usersYou')})</span>
                    ) : null}
                  </td>
                  <td>
                    {user.email}
                    {user.mfa_enabled ? (
                      <span
                        className="ml-1 inline-flex items-center gap-0.5 rounded bg-emerald-50 px-1 text-[11px] text-emerald-700"
                        title={t('mfa.title')}
                      >
                        <ShieldCheck size={11} /> MFA
                      </span>
                    ) : null}
                  </td>
                  <td>{t(`settings.roles.${user.role}`)}</td>
                  <td>{formatDate(user.created)}</td>
                  <td>
                    <div className="flex gap-0.5">
                      <button
                        type="button"
                        className="btn-ghost p-1 text-xs"
                        onClick={() => setEditing(user)}
                      >
                        {t('global.edit')}
                      </button>
                      {user.mfa_enabled && user.id !== me?.id ? (
                        <button
                          type="button"
                          className="btn-ghost p-1 text-amber-600"
                          title={t('mfa.reset')}
                          onClick={() => {
                            if (window.confirm(t('mfa.resetConfirm', { email: user.email })))
                              resetMfa.mutate(user.id)
                          }}
                        >
                          <ShieldOff size={14} />
                        </button>
                      ) : null}
                      {user.id !== me?.id ? (
                        user.block ? (
                          <button
                            type="button"
                            className="btn-ghost p-1 text-green-600"
                            title={t('global.confirm')}
                            onClick={() => unblock.mutate(user.id)}
                          >
                            <Check size={14} />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn-ghost p-1 text-red-500"
                            title={t('settings.blocked')}
                            onClick={() => block.mutate(user.id)}
                          >
                            <Ban size={14} />
                          </button>
                        )
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing ? (
        <UserModal
          user={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={invalidate}
        />
      ) : null}
    </div>
  )
}

function UserModal({
  user,
  onClose,
  onSaved,
}: {
  user: User | null
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState({
    email: user?.email ?? '',
    firstname: user?.firstname ?? '',
    lastname: user?.lastname ?? '',
    phone: user?.phone ?? '',
    role: (user?.role ?? 'manager') as Role,
    password: '',
  })
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () => {
      const payload = { ...form }
      if (!payload.password) delete (payload as Partial<typeof form>).password
      return user ? api.update(user.id, payload) : api.create(payload)
    },
    onSuccess: () => {
      onSaved()
      onClose()
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  return (
    <Modal
      open
      onClose={onClose}
      title={user ? t('global.edit') : t('settings.newUser')}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('global.cancel')}
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={save.isPending}
            onClick={() => {
              setError(null)
              save.mutate()
            }}
          >
            {save.isPending ? <Spinner /> : null}
            {t('global.save')}
          </button>
        </>
      }
    >
      <div className="space-y-2">
        {error ? <Alert variant="error">{error}</Alert> : null}

        <div className="grid gap-2 sm:grid-cols-2">
          <Field label={t('reservation.firstname')}>
            <Input
              value={form.firstname}
              onChange={(e) => setForm({ ...form, firstname: e.target.value })}
            />
          </Field>
          <Field label={t('reservation.surname')}>
            <Input
              value={form.lastname}
              onChange={(e) => setForm({ ...form, lastname: e.target.value })}
            />
          </Field>
        </div>

        <Field label={t('auth.email')}>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </Field>

        <div className="grid gap-2 sm:grid-cols-2">
          <Field label={t('reservation.phone')}>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </Field>
          <Field label={t('settings.role')}>
            <Select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as Role })}
            >
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {t(`settings.roles.${role}`)}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label={t('auth.password')}>
          <Input
            type="password"
            autoComplete="new-password"
            placeholder={user ? '••••••' : ''}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
        </Field>
      </div>
    </Modal>
  )
}
